import React, { useCallback, useEffect, useRef, memo, useMemo } from 'react';
import { ConditionalSolidLayer } from '../../components/ui/ConditionalSolidLayer';
import type { BlurTint } from '../../shared/theme/blurParity';
import { View, Text, StyleSheet, Platform, ActivityIndicator } from 'react-native';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  interpolate,
  withSpring,
  Extrapolation,
  interpolateColor,
  useAnimatedProps,
  withSequence,
  withTiming,
  withRepeat,
  Easing,
  cancelAnimation,
} from 'react-native-reanimated';
import { appTheme, useThemeColors, useThemeStore, type ThemeColors } from '../../shared/theme';
import { SvgIcon } from '../../components/common/SvgIcon';
import { ButtonLoader } from '../../components/load-main/ButtonLoader';
import { useTranslation, formatNumber } from '../../shared/i18n';
import { Clickable } from '../../components/common/Clickable';
import { blurIntensityForPlatform, FROSTED_BOTTOM_CHROME_RADIUS } from '../../shared/theme/sharedStyles';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { getMenuFlowBottomOverlayPaddingBottom, IOS_MENU_FLOW_BOTTOM_OVERLAY_PADDING_BOTTOM } from '../../navigation/mainTabs/androidFloatingTabBarLayout';

const BOTTOM_BLUR_MAX_INTENSITY = blurIntensityForPlatform(9);

const SAVE_CHIP_MIN_HEIGHT = 32;

/** Tab-bar clearance + horizontal inset. Used by: `MenuFlowBottomMacroBar`, `OrderScreen` checkout overlay. */
export const MENU_FLOW_BOTTOM_OVERLAY_PADDING_TOP = 10;
/** @deprecated Use `getMenuFlowBottomOverlayPaddingBottom(insets.bottom)` — kept for iOS static imports. */
export const MENU_FLOW_BOTTOM_OVERLAY_PADDING_BOTTOM = IOS_MENU_FLOW_BOTTOM_OVERLAY_PADDING_BOTTOM;
export const MENU_FLOW_BOTTOM_OVERLAY_PADDING_HORIZONTAL = 10;
/** Tuned to list `paddingBottom` for `MenuFlowScreen` so the last item clears the bar. */
const MENU_FLOW_LIST_EXTRA_CLEARANCE = 12;

/**
 * `contentContainerStyle.paddingBottom` for the menu `FlatList` when the macro+save bar is shown.
 * Used by: `MenuFlowScreen` (v3)
 */
export function getMenuListBottomContentPadding(safe_area_bottom: number) {
  const bottom_overlay_pad = getMenuFlowBottomOverlayPaddingBottom(safe_area_bottom);
  const ios_safe_extra = Platform.OS === 'android' ? 0 : safe_area_bottom;
  return (
    MENU_FLOW_BOTTOM_OVERLAY_PADDING_TOP +
    SAVE_CHIP_MIN_HEIGHT +
    bottom_overlay_pad +
    ios_safe_extra +
    MENU_FLOW_LIST_EXTRA_CLEARANCE
  );
}

export type MenuSaveBarStatus = 'synced' | 'dirty' | 'saving' | 'saved' | 'error';

function usePrevious<T>(value: T): T | undefined {
  const ref = useRef<T | undefined>(undefined);
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
}

type StatusColors = { bg: string; text: string; border: string; icon: string; breathe: boolean };

function getStatusColors(status: MenuSaveBarStatus, c: ThemeColors): StatusColors {
  const on_p = c.onPrimary;
  switch (status) {
    case 'synced':
      return { bg: c.borderLight, text: c.textSecondary, border: c.border, icon: c.textSecondary, breathe: false };
    case 'dirty':
      return { bg: c.primary, text: on_p, border: c.primary, icon: on_p, breathe: true };
    case 'saving':
      return { bg: c.primary, text: on_p, border: c.primary, icon: on_p, breathe: true };
    case 'saved':
      return { bg: c.success, text: c.onSuccess, border: c.success, icon: c.onSuccess, breathe: false };
    case 'error':
      return { bg: c.danger, text: c.onError, border: c.danger, icon: c.onError, breathe: true };
    default:
      return { bg: c.primary, text: on_p, border: c.primary, icon: on_p, breathe: false };
  }
}

const MenuSaveActionChip = memo(
  ({ status, disabled, onPress }: { status: MenuSaveBarStatus; disabled: boolean; onPress: () => void }) => {
    const { t } = useTranslation();
    const theme_c = useThemeColors() ?? appTheme.colors;
    const col = getStatusColors(status, theme_c);
    const prev_status = usePrevious(status);

    const label =
      status === 'dirty'
        ? t('menu.saveUnsaved')
        : status === 'saving'
          ? t('menu.saveSaving')
          : status === 'saved'
            ? t('menu.saveSaved')
            : status === 'error'
              ? t('menu.saveRetry')
              : t('menu.saveSynced');

    const press_scale = useSharedValue(1);
    const breathe = useSharedValue(0);
    const saved_bump = useSharedValue(0);
    const breathe_on = useSharedValue(0);
    const err_wiggle = useSharedValue(0);
    const saved_icon_pop = useSharedValue(0);

    useEffect(() => {
      const on = status === 'dirty' || status === 'saving' || status === 'error' ? 1 : 0;
      breathe_on.value = on;
    }, [status, breathe_on]);

    useEffect(() => {
      const on = status === 'dirty' || status === 'saving' || status === 'error';
      if (on) {
        breathe.value = withRepeat(
          withSequence(
            withTiming(1, { duration: 750, easing: Easing.inOut(Easing.ease) }),
            withTiming(0, { duration: 750, easing: Easing.inOut(Easing.ease) }),
          ),
          -1,
          true,
        );
        return () => {
          cancelAnimation(breathe);
          breathe.value = 0;
        };
      }
      cancelAnimation(breathe);
      breathe.value = 0;
    }, [status, breathe]);

    useEffect(() => {
      if (status === 'saved' && prev_status != null && prev_status !== 'saved') {
        saved_bump.value = 0;
        saved_bump.value = withSequence(
          withTiming(1, { duration: 140, easing: Easing.out(Easing.cubic) }),
          withTiming(0, { duration: 1000, easing: Easing.inOut(Easing.ease) }),
        );
        saved_icon_pop.value = 0.88;
        saved_icon_pop.value = withSequence(
          withSpring(1.2, { damping: 9, stiffness: 360 }),
          withTiming(1, { duration: 220, easing: Easing.out(Easing.cubic) }),
        );
      }
    }, [status, prev_status, saved_bump, saved_icon_pop]);

    useEffect(() => {
      if (status === 'error' && (prev_status === 'saving' || prev_status === 'dirty' || prev_status === 'synced')) {
        err_wiggle.value = 0;
        err_wiggle.value = withSequence(
          withTiming(14, { duration: 70, easing: Easing.inOut(Easing.ease) }),
          withTiming(-10, { duration: 55, easing: Easing.inOut(Easing.ease) }),
          withTiming(6, { duration: 55, easing: Easing.inOut(Easing.ease) }),
          withTiming(0, { duration: 130, easing: Easing.out(Easing.cubic) }),
        );
        return;
      }
      if (status !== 'error') {
        err_wiggle.value = 0;
      }
    }, [status, prev_status, err_wiggle]);

    useEffect(() => {
      if (status !== 'saved') {
        saved_icon_pop.value = 1;
      }
    }, [status, saved_icon_pop]);

    const wrap_style = useAnimatedStyle(() => {
      const br = 1 + 0.02 * breathe.value * breathe_on.value + 0.08 * saved_bump.value;
      return { transform: [{ scale: br * press_scale.value }] };
    });

    const error_icon_style = useAnimatedStyle(() => ({
      transform: [{ rotate: `${err_wiggle.value}deg` }],
    }));

    const saved_icon_style = useAnimatedStyle(() => ({
      transform: [{ scale: saved_icon_pop.value }],
    }));

    const on_press = useCallback(() => {
      if (disabled) {
        return;
      }
      press_scale.value = withSequence(
        withTiming(0.94, { duration: 85, easing: Easing.out(Easing.cubic) }),
        withSpring(1, { damping: 11, stiffness: 280 }),
      );
      onPress();
    }, [disabled, onPress, press_scale]);

    return (
      <View style={saveChipStyles.chipRow}>
        <Animated.View
          style={[
            saveChipStyles.chipWrap,
            { borderColor: col.border, backgroundColor: 'transparent' },
            wrap_style,
          ]}
        >
          <Clickable
            onPress={on_press}
            disabled={disabled}
            contentStyle={saveChipStyles.chipContent}
            style={[
              saveChipStyles.chip,
              { backgroundColor: col.bg, borderColor: col.border },
              disabled && saveChipStyles.chipDisabled,
            ]}
            accessibilityLabel={label}
            accessibilityRole="button"
            accessibilityState={{ disabled }}
          >
            {status === 'saving' ? (
              <ActivityIndicator size="small" color={col.text} style={saveChipStyles.chipIcon} />
            ) : status === 'synced' ? (
              <SvgIcon name="ellipse-outline" size={16} color={col.icon} style={saveChipStyles.chipIcon} />
            ) : status === 'saved' ? (
              <Animated.View style={saved_icon_style}>
                <SvgIcon name="checks" size={18} color={col.icon} style={saveChipStyles.chipIcon} />
              </Animated.View>
            ) : status === 'error' ? (
              <Animated.View style={error_icon_style}>
                <SvgIcon name="refresh" size={14} color={col.icon} style={saveChipStyles.chipIcon} />
              </Animated.View>
            ) : (
              <SvgIcon name="plus" size={14} color={col.icon} style={saveChipStyles.chipIcon} />
            )}
            <Text style={[saveChipStyles.chipText, { color: col.text }]} numberOfLines={1} ellipsizeMode="tail">
              {status === 'saving' ? t('menu.saveSaving') : label}
            </Text>
          </Clickable>
        </Animated.View>
      </View>
    );
  },
);

MenuSaveActionChip.displayName = 'MenuSaveActionChip';

const saveChipStyles = StyleSheet.create({
  chipRow: { flexShrink: 0, alignItems: 'center', justifyContent: 'center' },
  chipContent: {
    minHeight: SAVE_CHIP_MIN_HEIGHT,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    paddingHorizontal: 2,
  },
  chipWrap: { maxWidth: 152, borderWidth: 1, borderRadius: 10, overflow: 'hidden' },
  chip: {
    minHeight: SAVE_CHIP_MIN_HEIGHT,
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 8,
    borderWidth: 0,
    alignItems: 'center',
    justifyContent: 'center',
  },
  chipDisabled: { opacity: 0.55 },
  chipIcon: { alignSelf: 'center' },
  chipText: {
    fontSize: 12,
    fontWeight: '600',
    flexShrink: 1,
    maxWidth: 100,
    lineHeight: 16,
    ...Platform.select({ android: { includeFontPadding: false } }),
  },
});

function createMenuFlowBottomMacroBarStyles(c: ThemeColors) {
  return StyleSheet.create({
    bottomMacroCard: {
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      zIndex: 99,
    },
    bottomMacroCardPadding: {
      paddingTop: MENU_FLOW_BOTTOM_OVERLAY_PADDING_TOP,
      paddingHorizontal: MENU_FLOW_BOTTOM_OVERLAY_PADDING_HORIZONTAL,
      alignItems: 'center',
      justifyContent: 'center',
    },
    mainHRow: {
      width: '100%',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 4,
    },
    macroCluster: {
      flex: 1,
      minWidth: 0,
      flexDirection: 'row',
      alignItems: 'center',
    },
    bottomMacroRow: {
      flexDirection: 'row',
      justifyContent: 'space-evenly',
      alignItems: 'center',
      flex: 1,
    },
    bottomMacroItem: {
      alignItems: 'center',
    },
    bottomMacroItemCalories: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: c.calorieUltra,
      paddingHorizontal: 10,
      paddingVertical: 5,
      borderRadius: 10,
      gap: 4,
    },
    bottomMacroValueProtein: {
      fontSize: 14,
      fontWeight: '700',
      color: c.protein,
    },
    bottomMacroValueCarbs: {
      fontSize: 14,
      fontWeight: '700',
      color: c.carbs,
    },
    bottomMacroValueFats: {
      fontSize: 14,
      fontWeight: '700',
      color: c.fats,
    },
    bottomMacroLabel: {
      fontSize: 12,
      color: c.textSecondary,
      marginTop: 2,
    },
    bottomMacroValueCalories: {
      fontSize: 18,
      fontWeight: '700',
      color: c.calorie,
    },
    bottomMacroLabelCalories: {
      fontSize: 12,
      fontWeight: '600',
      color: c.textSecondary,
      marginTop: 2,
    },
    bottomCompleteBadge: {
      height: 25,
      width: 25,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: c.success,
      borderRadius: 20,
    },
    savingRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      marginBottom: 4,
    },
    savingText: {
      fontSize: 12,
      color: c.textSecondary,
    },
  });
}

interface MenuFlowBottomMacroBarProps {
  macroTotals: {
    calories: number;
    protein: number;
    carbs: number;
    fats: number;
  };
  isComplete: boolean;
  isVisible: boolean;
  useSolidBackground?: boolean;
  isSyncing?: boolean;
  onPressSave?: () => void;
  saveStatus?: MenuSaveBarStatus;
  isSaveLoading?: boolean;
  saveButtonDisabled?: boolean;
  /** When false (v2: nutrition UI off), hide macro totals but keep save/sync chip. */
  showMacroTotals?: boolean;
}

export const MenuFlowBottomMacroBar = memo(
  ({
    macroTotals,
    isComplete,
    isVisible,
    useSolidBackground = false,
    isSyncing = false,
    onPressSave,
    saveStatus = 'synced',
    isSaveLoading = false,
    saveButtonDisabled = false,
    showMacroTotals = true,
  }: MenuFlowBottomMacroBarProps) => {
  const { t } = useTranslation();
  const insets = useSafeAreaInsets();
  const bottom_overlay_padding = getMenuFlowBottomOverlayPaddingBottom(insets.bottom);
  const appearance = useThemeStore(s => s.appearance);
  const colors = useThemeColors() ?? appTheme.colors;
  const styles = useMemo(() => createMenuFlowBottomMacroBarStyles(colors), [colors]);
  const bottom_bar_blur_tint: BlurTint = appearance === 'dark' ? 'systemChromeMaterialDark' : 'systemChromeMaterialLight';

  const transition = useSharedValue(0);
  const transitionComplete = useSharedValue(0);

  useEffect(() => {
    transition.value = withSpring(isVisible ? 1 : 0, {
      damping: 20,
      stiffness: 100,
    });
  }, [isVisible]);

  useEffect(() => {
    transitionComplete.value = withSpring(isComplete ? 1 : 0, {
      damping: 15,
      stiffness: 100,
    });
  }, [isComplete]);
  const animatedStyle = useAnimatedStyle(() => {
    return {
      opacity: transition.value,
      transform: [{ translateY: interpolate(transition.value, [0, 1], [50, 0], Extrapolation.CLAMP) }],
    };
  });
  const completionStyle = useAnimatedStyle(() => ({
    opacity: transitionComplete.value,
    transform: [{ scale: interpolate(transitionComplete.value, [0, 0.5, 1], [0, 1.2, 1], Extrapolation.CLAMP) }],
  }));
  const backgroundColorStyle = useAnimatedStyle(() => {
    return {
      backgroundColor: interpolateColor(transitionComplete.value, [0, 1], [colors.primaryUltra, colors.primaryFade], 'RGB'),
    };
  });

  const v3_status: MenuSaveBarStatus = saveStatus;
  const effective_saving = saveStatus === 'saving' || isSaveLoading;

  return (
    <Animated.View style={[styles.bottomMacroCard, styles.bottomMacroCardPadding, { paddingBottom: bottom_overlay_padding }, animatedStyle]}>
      <View
        style={[
          StyleSheet.absoluteFill,
          {
            borderTopLeftRadius: 24,
            borderTopRightRadius: 24,
            backgroundColor: colors.surface,
          },
        ]}
      />
      <Animated.View style={[StyleSheet.absoluteFill, backgroundColorStyle]} pointerEvents="none" />
      {isSyncing && !onPressSave && (
        <View style={styles.savingRow}>
          <ButtonLoader size={18} color={colors.primary} />
          <Text style={styles.savingText}>{t('account.saving')}</Text>
        </View>
      )}
      <View style={styles.mainHRow}>
        {showMacroTotals ? (
          <View style={styles.macroCluster}>
            <View style={styles.bottomMacroRow}>
              <View style={styles.bottomMacroItemCalories}>
                <Text style={styles.bottomMacroValueCalories} numberOfLines={1} adjustsFontSizeToFit>
                  {formatNumber(macroTotals.calories)}
                </Text>
                <Text style={styles.bottomMacroLabelCalories}>{t('menu.kcalLabel')}</Text>
              </View>

              <View style={styles.bottomMacroItem}>
                <Text style={styles.bottomMacroValueProtein} numberOfLines={1}>
                  {formatNumber(macroTotals.protein)}g
                </Text>
                <Text style={styles.bottomMacroLabel} numberOfLines={1}>
                  {t('dashboard.protein')}
                </Text>
              </View>

              <View style={styles.bottomMacroItem}>
                <Text style={styles.bottomMacroValueCarbs} numberOfLines={1}>
                  {formatNumber(macroTotals.carbs)}g
                </Text>
                <Text style={styles.bottomMacroLabel} numberOfLines={1}>
                  {t('dashboard.carbs')}
                </Text>
              </View>

              <View style={styles.bottomMacroItem}>
                <Text style={styles.bottomMacroValueFats} numberOfLines={1}>
                  {formatNumber(macroTotals.fats)}g
                </Text>
                <Text style={styles.bottomMacroLabel} numberOfLines={1}>
                  {t('dashboard.fat')}
                </Text>
              </View>
            </View>
          </View>
        ) : null}
        <Animated.View style={[styles.bottomCompleteBadge, completionStyle]}>
          <SvgIcon name="check" size={16} color={colors.white} />
        </Animated.View>
        {onPressSave ? (
          <MenuSaveActionChip
            status={v3_status}
            disabled={saveButtonDisabled || effective_saving}
            onPress={onPressSave}
          />
        ) : null}
      </View>
    </Animated.View>
  );
  },
);
